import { useTranslation } from 'react-i18next';

import { useToast } from '@/components/ui/use-toast';

import type { PayoutStage } from '@/app/manage/crypto-payments/components/types';

import { trpc } from './trpc';

/**
 * Hooks for the on-chain crypto payments **platform** API (crypto-api), the
 * staff-only surface behind `/manage/crypto-payments`: platform wallets, the
 * default revenue share, the merchants enabled and their payments.
 *
 * Separate from `crypto-onchain.ts` (the merchant surface over
 * `trpc.cryptoOnchain.*`) and from `crypto.ts` (the Rampex fiat-to-crypto rail).
 */

const STALE_TIME_MS = 30 * 1000;
const DEFAULT_PAYMENTS_LIMIT = 50;

export function useCryptoOnchainPlatformSettings() {
  return trpc.cryptoOnchainPlatform.getSettings.useQuery(undefined, { staleTime: STALE_TIME_MS });
}

export function useCryptoOnchainPlatformMerchants() {
  return trpc.cryptoOnchainPlatform.listMerchants.useQuery(undefined, { staleTime: STALE_TIME_MS });
}

/** Newest first, across every store unless `storeId` narrows it. */
export function useCryptoOnchainPlatformPayments(storeId?: string, limit: number = DEFAULT_PAYMENTS_LIMIT) {
  return trpc.cryptoOnchainPlatform.listPayments.useQuery(
    { ...(storeId ? { storeId } : {}), limit },
    { staleTime: STALE_TIME_MS },
  );
}

/**
 * Shared invalidation: a settings change moves every merchant's effective
 * share, and a merchant change is mirrored in the settings' view of the
 * platform, so refresh both rather than guessing.
 */
function useInvalidatePlatform() {
  const utils = trpc.useUtils();
  return () => {
    void utils.cryptoOnchainPlatform.getSettings.invalidate();
    void utils.cryptoOnchainPlatform.listMerchants.invalidate();
  };
}

export function useUpdateCryptoOnchainPlatformSettings() {
  const { t } = useTranslation('manageCryptoPayments');
  const { toast } = useToast();
  const invalidate = useInvalidatePlatform();
  return trpc.cryptoOnchainPlatform.updateSettings.useMutation({
    onSuccess: () => {
      invalidate();
      toast({ title: t('toasts.settingsSaved') });
    },
    onError: (error: { message: string }) => {
      toast({ title: t('toasts.settingsSaveFailed'), description: error.message, variant: 'destructive' });
    },
  });
}

export function useSetCryptoOnchainMerchantShare() {
  const { t } = useTranslation('manageCryptoPayments');
  const { toast } = useToast();
  const invalidate = useInvalidatePlatform();
  return trpc.cryptoOnchainPlatform.setMerchantShare.useMutation({
    onSuccess: () => {
      invalidate();
      toast({ title: t('toasts.merchantShareSaved') });
    },
    onError: (error: { message: string }) => {
      toast({
        title: t('toasts.merchantShareSaveFailed'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/**
 * "Re-sync": the server fetches the crypto-api session and reconciles the mirror row and the order
 * (a lost webhook, a late payment). Only the payments list changes, so only it is refreshed.
 */
export function useResyncCryptoOnchainPayment() {
  const { t } = useTranslation('manageCryptoPayments');
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.cryptoOnchainPlatform.resyncPayment.useMutation({
    onSuccess: () => {
      void utils.cryptoOnchainPlatform.listPayments.invalidate();
      toast({ title: t('toasts.paymentResynced') });
    },
    onError: (error: { message: string }) => {
      toast({ title: t('toasts.paymentResyncFailed'), description: error.message, variant: 'destructive' });
    },
  });
}

export function useSetCryptoOnchainMerchantPayoutAddresses() {
  const { t } = useTranslation('manageCryptoPayments');
  const { toast } = useToast();
  const invalidate = useInvalidatePlatform();
  return trpc.cryptoOnchainPlatform.setMerchantPayoutAddresses.useMutation({
    onSuccess: () => {
      invalidate();
      toast({ title: t('toasts.payoutAddressesSaved') });
    },
    onError: (error: { message: string }) => {
      toast({
        title: t('toasts.payoutAddressesSaveFailed'),
        description: error.message,
        variant: 'destructive',
      });
    },
  });
}

/** The platform gas wallets with a live balance per chain (crypto-api reads them on every call). */
export function useCryptoOnchainGasWallets() {
  return trpc.cryptoOnchainPlatform.getGasWallets.useQuery(undefined, { staleTime: STALE_TIME_MS });
}

/** Live reads on every chain crypto-api offers; `refetch` checks again. */
export function useCryptoOnchainChainHealth() {
  return trpc.cryptoOnchainPlatform.getChainHealth.useQuery(undefined, { staleTime: STALE_TIME_MS });
}

/** Sweeps newest first, read live from crypto-api; `stage` narrows to one stage (e.g. `failed`). */
export function useCryptoOnchainPayouts(params: { stage?: PayoutStage; cursor?: string; limit?: number } = {}) {
  return trpc.cryptoOnchainPlatform.listPayouts.useQuery(
    {
      limit: params.limit ?? DEFAULT_PAYMENTS_LIMIT,
      ...(params.stage ? { stage: params.stage } : {}),
      ...(params.cursor ? { cursor: params.cursor } : {}),
    },
    { staleTime: STALE_TIME_MS },
  );
}

/** Superadmin: queues a manual sweep of one payout; the list refreshes so the new attempt shows. */
export function useRetryCryptoOnchainPayout() {
  const { t } = useTranslation('manageCryptoPayments');
  const { toast } = useToast();
  const utils = trpc.useUtils();
  return trpc.cryptoOnchainPlatform.retryPayout.useMutation({
    onSuccess: () => {
      void utils.cryptoOnchainPlatform.listPayouts.invalidate();
      toast({ title: t('toasts.payoutRetryQueued') });
    },
    onError: (error: { message: string }) => {
      toast({ title: t('toasts.payoutRetryFailed'), description: error.message, variant: 'destructive' });
    },
  });
}
